import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { megaPrizeAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Trophy, Settings, List, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MegaPrizeManagement = () => {
  const [config, setConfig] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [winner, setWinner] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]); // NOVO
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [selectedWinners, setSelectedWinners] = useState([]); // NOVO
  const [loading, setLoading] = useState(true);
  const [drawnNumbers, setDrawnNumbers] = useState(['', '', '', '', '', '']);
  const [filters, setFilters] = useState({ kako_id: '', start_date: '', end_date: '' }); // NOVO
  
  const [configForm, setConfigForm] = useState({
    initial_prize: 100000,
    min_order_coins: 10000,
    prize_increment: 100,
    rules_text: '' // NOVO
  });

  useEffect(() => {
    console.log('🎰 MegaPrizeManagement montado!');
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, ticketsRes, winnerRes, winnersHistoryRes] = await Promise.all([
        megaPrizeAPI.getConfig(),
        megaPrizeAPI.getAllTickets({}),
        megaPrizeAPI.getCurrentWinner(),
        megaPrizeAPI.getWinnersHistory(20)
      ]);
      
      setConfig(configRes.data);
      setTickets(ticketsRes.data);
      setWinner(winnerRes.data);
      setWinnersHistory(winnersHistoryRes.data);
      
      if (configRes.data) {
        setConfigForm({
          initial_prize: configRes.data.initial_prize || 100000,
          min_order_coins: configRes.data.min_order_coins || 10000,
          prize_increment: configRes.data.prize_increment || 100,
          rules_text: configRes.data.rules_text || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEvent = async (active) => {
    try {
      await megaPrizeAPI.updateConfig({ active });
      toast.success(`Evento ${active ? 'ativado' : 'desativado'}!`);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await megaPrizeAPI.updateConfig(configForm);
      toast.success('Configuração salva!');
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleFinalize = async () => {
    const numbers = drawnNumbers.map(n => parseInt(n)).filter(n => !isNaN(n) && n >= 1 && n <= 60);
    
    if (numbers.length !== 6) {
      toast.error('Informe exatamente 6 dezenas (01-60)');
      return;
    }
    
    if (!window.confirm(`Finalizar evento com números: ${numbers.join('-')}?`)) {
      return;
    }
    
    try {
      const result = await megaPrizeAPI.finalize(numbers);
      toast.success('Evento finalizado!');
      setDrawnNumbers(['', '', '', '', '', '']);
      loadData();
    } catch (error) {
      toast.error('Erro ao finalizar');
    }
  };

  const handleRestart = async () => {
    const choice = window.confirm(
      '🔄 REINICIAR EVENTO:\n\n' +
      'Clique OK para CONTINUAR de onde parou (mantém tickets)\n' +
      'Clique CANCELAR para LIMPAR TUDO'
    );
    
    let clearData = false;
    
    if (!choice) {
      // Confirma se quer mesmo limpar
      const confirmClear = window.confirm(
        '🗑️ TEM CERTEZA?\n\n' +
        'Isso vai APAGAR todos os tickets!\n\n' +
        'OK = APAGAR TUDO\n' +
        'CANCELAR = Não fazer nada'
      );
      
      if (!confirmClear) return;
      clearData = true;
    }
    
    try {
      await megaPrizeAPI.restart(clearData);
      toast.success(clearData ? '🗑️ Evento resetado do zero!' : '🔄 Evento continuando!');
      loadData();
    } catch (error) {
      toast.error('Erro ao reiniciar');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedTickets.length === 0) {
      toast.error('Selecione tickets');
      return;
    }
    
    if (!window.confirm(`Excluir ${selectedTickets.length} ticket(s)?`)) return;
    
    try {
      await megaPrizeAPI.deleteTickets(selectedTickets);
      toast.success(`${selectedTickets.length} ticket(s) excluído(s)!`);
      setSelectedTickets([]);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleExportTickets = async () => {
    try {
      await megaPrizeAPI.exportTicketsCSV();
      toast.success('Exportando TODOS os tickets...');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-12"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Configuração + Status */}
      <Card className="border-2 border-purple-300">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuração do Evento
            </CardTitle>
            {config && (
              <div className="flex items-center gap-3">
                <Label className="font-bold">Status do Evento:</Label>
                <Switch checked={config.active} onCheckedChange={handleToggleEvent} />
                <Badge variant={config.active ? 'default' : 'secondary'} className="text-sm px-3 py-1">
                  {config.active ? '✅ ATIVO' : '❌ INATIVO'}
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Prêmio Inicial (moedas)</Label>
            <Input
              type="number"
              value={configForm.initial_prize}
              onChange={(e) => setConfigForm({...configForm, initial_prize: parseInt(e.target.value)})}
            />
          </div>
          <div>
            <Label>Mínimo Moedas (pedido válido)</Label>
            <Input
              type="number"
              value={configForm.min_order_coins}
              onChange={(e) => setConfigForm({...configForm, min_order_coins: parseInt(e.target.value)})}
            />
          </div>
          <div>
            <Label>Incremento por Pedido</Label>
            <Input
              type="number"
              value={configForm.prize_increment}
              onChange={(e) => setConfigForm({...configForm, prize_increment: parseInt(e.target.value)})}
            />
          </div>
          <div className="md:col-span-3">
            <Label>Regras do Evento (visível para usuários)</Label>
            <textarea
              className="w-full border rounded px-3 py-2 min-h-32"
              placeholder="Digite as regras do evento..."
              value={configForm.rules_text}
              onChange={(e) => setConfigForm({...configForm, rules_text: e.target.value})}
            />
          </div>
          <div className="md:col-span-3">
            <Button onClick={handleSaveConfig} className="bg-purple-600">
              Salvar Configuração
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prêmio Acumulado */}
      {config && config.active && (
        <Card className="border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-orange-50">
          <CardContent className="p-6 text-center">
            <Trophy className="h-12 w-12 mx-auto text-yellow-600 mb-2" />
            <p className="text-sm text-gray-600">Prêmio Acumulado</p>
            <p className="text-4xl font-black text-yellow-600">{config.current_prize?.toLocaleString() || 0} 🎁</p>
            <p className="text-xs text-gray-500 mt-2">{config.total_tickets || 0} tickets gerados</p>
            
            {/* Ajustar Prêmio Manualmente */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <p className="text-xs font-bold text-gray-600">Ajustar Prêmio Manualmente</p>
              <div className="flex gap-2 justify-center">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={async () => {
                    const valor = prompt('Adicionar moedas (número positivo):');
                    if (valor && !isNaN(valor)) {
                      try {
                        await megaPrizeAPI.updateConfig({ 
                          current_prize: (config.current_prize || 0) + parseInt(valor)
                        });
                        toast.success('Prêmio atualizado!');
                        loadData();
                      } catch (error) {
                        toast.error('Erro');
                      }
                    }
                  }}
                >
                  ➕ Adicionar
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={async () => {
                    const valor = prompt('Remover moedas (número positivo):');
                    if (valor && !isNaN(valor)) {
                      try {
                        await megaPrizeAPI.updateConfig({ 
                          current_prize: Math.max(0, (config.current_prize || 0) - parseInt(valor))
                        });
                        toast.success('Prêmio atualizado!');
                        loadData();
                      } catch (error) {
                        toast.error('Erro');
                      }
                    }
                  }}
                >
                  ➖ Remover
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Finalizar / Reiniciar */}
      <Card>
        <CardHeader>
          <CardTitle>Gerenciar Evento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sortear */}
          <div>
            <Label>Números Sorteados (01-60)</Label>
            <div className="grid grid-cols-6 gap-2">
              {drawnNumbers.map((num, idx) => (
                <Input
                  key={idx}
                  type="number"
                  min="1"
                  max="60"
                  placeholder={(idx + 1).toString()}
                  value={num}
                  onChange={(e) => {
                    const newNumbers = [...drawnNumbers];
                    newNumbers[idx] = e.target.value;
                    setDrawnNumbers(newNumbers);
                  }}
                  className="text-center"
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleFinalize} className="bg-green-600">
              Finalizar e Sortear
            </Button>
            <Button onClick={handleRestart} variant="destructive">
              Reiniciar Evento
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Tickets */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Histórico de Tickets ({tickets.length})
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleExportTickets} variant="outline">
                Exportar CSV
              </Button>
              {selectedTickets.length > 0 && (
                <Button size="sm" variant="destructive" onClick={handleDeleteSelected}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Excluir {selectedTickets.length}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filtros */}
          <div className="grid md:grid-cols-3 gap-2">
            <Input
              placeholder="KAKO ID..."
              value={filters.kako_id}
              onChange={(e) => setFilters({...filters, kako_id: e.target.value})}
            />
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
            />
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
            />
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2">
                    <Checkbox
                      checked={tickets.length > 0 && selectedTickets.length === tickets.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedTickets(tickets.map(t => t.id));
                        } else {
                          setSelectedTickets([]);
                        }
                      }}
                    />
                  </th>
                  <th className="text-left p-2">Nickname</th>
                  <th className="text-left p-2">KAKO ID</th>
                  <th className="text-left p-2">Pedido</th>
                  <th className="text-left p-2">Números</th>
                  <th className="text-left p-2">Data</th>
                </tr>
              </thead>
              <tbody>
                {tickets
                  .filter(t => !filters.kako_id || t.kako_id.includes(filters.kako_id))
                  .filter(t => !filters.start_date || new Date(t.created_at) >= new Date(filters.start_date))
                  .filter(t => !filters.end_date || new Date(t.created_at) <= new Date(filters.end_date))
                  .map((ticket) => (
                  <tr key={ticket.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <Checkbox
                        checked={selectedTickets.includes(ticket.id)}
                        onCheckedChange={() => {
                          if (selectedTickets.includes(ticket.id)) {
                            setSelectedTickets(selectedTickets.filter(id => id !== ticket.id));
                          } else {
                            setSelectedTickets([...selectedTickets, ticket.id]);
                          }
                        }}
                      />
                    </td>
                    <td className="p-2 font-medium">{ticket.kako_nickname}</td>
                    <td className="p-2 font-mono text-xs">{ticket.kako_id}</td>
                    <td className="p-2 font-mono text-xs text-gray-600">#{ticket.order_id?.slice(0, 8) || 'Manual'}</td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        {ticket.numbers_str.split('-').map((num, idx) => (
                          <span key={idx} className="inline-block w-7 h-7 rounded-full bg-yellow-500 text-white text-xs font-bold flex items-center justify-center">
                            {num}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-2 text-xs">{new Date(ticket.created_at).toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Ganhador Atual */}
      {winner && (
        <Card className="border-2 border-green-400">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>🏆 Ganhador Atual</CardTitle>
              <Button size="sm" variant="destructive" onClick={async () => {
                console.log('🗑️ Tentando remover ganhador:', winner.id);
                if (!window.confirm('Remover ganhador?')) return;
                try {
                  const result = await megaPrizeAPI.deleteWinnersBulk([winner.id]);
                  console.log('✅ Resultado:', result);
                  toast.success('Ganhador removido!');
                  loadData();
                } catch (error) {
                  console.error('❌ Erro ao remover:', error);
                  toast.error('Erro ao remover');
                }
              }}>
                <Trash2 className="h-4 w-4 mr-1" />
                Remover
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              {winner.winner_avatar && (
                <img src={winner.winner_avatar} alt="" className="w-16 h-16 rounded-full" />
              )}


      {/* Histórico de Ganhadores */}
      {winnersHistory.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>📜 Histórico de Ganhadores</CardTitle>
              {selectedWinners.length > 0 && (
                <Button size="sm" variant="destructive" onClick={async () => {
                  if (!window.confirm(`Excluir ${selectedWinners.length} ganhador(es)?`)) return;
                  try {
                    await megaPrizeAPI.deleteWinnersBulk(selectedWinners);
                    toast.success('Ganhadores removidos!');
                    setSelectedWinners([]);
                    loadData();
                  } catch (error) {
                    toast.error('Erro ao excluir');
                  }
                }}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Excluir {selectedWinners.length}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2">
                      <Checkbox
                        checked={winnersHistory.length > 0 && selectedWinners.length === winnersHistory.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedWinners(winnersHistory.map(w => w.id));
                          } else {
                            setSelectedWinners([]);
                          }
                        }}
                      />
                    </th>
                    <th className="text-left p-2">Ganhador</th>
                    <th className="text-left p-2">Prêmio</th>
                    <th className="text-left p-2">Números</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-left p-2">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {winnersHistory.map((w) => (
                    <tr key={w.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <Checkbox
                          checked={selectedWinners.includes(w.id)}
                          onCheckedChange={() => {
                            if (selectedWinners.includes(w.id)) {
                              setSelectedWinners(selectedWinners.filter(id => id !== w.id));
                            } else {
                              setSelectedWinners([...selectedWinners, w.id]);
                            }
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {w.winner_avatar && <img src={w.winner_avatar} className="w-8 h-8 rounded-full" />}
                          <div>
                            <div className="font-medium">{w.winner_nickname || 'SEM GANHADOR'}</div>
                            <div className="text-xs text-gray-500">{w.winner_kako_id || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 font-bold text-green-600">{w.prize_amount?.toLocaleString() || 0} 🎁</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          {w.drawn_numbers_str?.split('-').map((num, idx) => (
                            <span key={idx} className="w-6 h-6 rounded-full bg-yellow-500 text-white text-xs font-bold flex items-center justify-center">
                              {num}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-xs">
                          {w.winner_type === '6_match' && '6 acertos'}
                          {w.winner_type === '5_match' && '5 acertos'}
                          {w.winner_type === '4_match' && '4 acertos'}
                          {w.winner_type === 'accumulated' && 'Acumulado'}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs">{new Date(w.event_end_date).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

              <div>
                <p className="text-2xl font-bold">{winner.winner_nickname || 'SEM GANHADOR'}</p>
                <p className="text-sm text-gray-600">KAKO ID: {winner.winner_kako_id || '-'}</p>
              </div>
            </div>
            <p className="text-xl font-bold text-green-600">Prêmio: {winner.prize_amount?.toLocaleString() || 0} 🎁</p>
            <p className="text-sm">Números: {winner.drawn_numbers_str}</p>
            <p className="text-xs text-gray-500">Tipo: {winner.winner_type}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MegaPrizeManagement;
